/* ============================================
   TFS Wheels — Cart Drawer
   Listens to `cart:updated` PubSub event from product-form.js.
   Auto-add safe: cart-interceptor.js's monkey-patched fetch
   completes before this PubSub event fires.
   Does NOT auto-open while the customer is already on the /cart page.
   ============================================ */

(function() {
  'use strict';

  var drawer = document.getElementById('CartDrawer');
  if (!drawer) return;

  var body = drawer.querySelector('[data-cart-drawer-body]');
  var footer = drawer.querySelector('[data-cart-drawer-footer]');
  var emptyState = drawer.querySelector('[data-cart-drawer-empty]');
  var subtotalEl = drawer.querySelector('[data-cart-drawer-subtotal]');

  /* --- State --- */
  var isOpen = false;
  var lastFocused = null;

  /* The full /cart page shows the cart already, so the slide-out drawer is
     redundant there. Used to suppress auto-open on cart mutations. */
  function onCartPage() {
    return window.location.pathname === '/cart';
  }

  /* --- Helpers --- */
  function formatMoney(cents) {
    /* Match Shopify default — TFS uses USD with 2 decimals. */
    var v = (cents / 100).toFixed(2);
    return '$' + v.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  }

  function el(tag, attrs, children) {
    /* Tiny createElement helper. attrs sets via setAttribute (safe).
       children: array of nodes/strings. Strings become text nodes (safe). */
    var node = document.createElement(tag);
    if (attrs) {
      Object.keys(attrs).forEach(function(k) {
        if (k === 'class') node.className = attrs[k];
        else if (k === 'text') node.textContent = attrs[k];
        else node.setAttribute(k, attrs[k]);
      });
    }
    (children || []).forEach(function(c) {
      if (c == null) return;
      if (typeof c === 'string') node.appendChild(document.createTextNode(c));
      else node.appendChild(c);
    });
    return node;
  }

  function clear(node) {
    while (node.firstChild) node.removeChild(node.firstChild);
  }

  /* --- Open / close --- */
  function open() {
    if (isOpen) return;
    isOpen = true;
    lastFocused = document.activeElement;
    drawer.classList.add('is-open');
    drawer.setAttribute('aria-hidden', 'false');
    document.body.style.overflow = 'hidden';
    var closeBtn = drawer.querySelector('.cart-drawer__close');
    if (closeBtn) closeBtn.focus();
    fetchCartAndRender();
  }

  function close() {
    if (!isOpen) return;
    isOpen = false;
    drawer.classList.remove('is-open');
    drawer.setAttribute('aria-hidden', 'true');
    document.body.style.overflow = '';
    if (lastFocused && lastFocused.focus) lastFocused.focus();
  }

  /* --- Compare-at (retail) map ----------------------------------------------
     /cart.js omits variant.compare_at_price, so a plain on-sale line (compare-at
     > price, no discount allocation) is indistinguishable from full price. We
     fetch the retail prices via the Section Rendering API
     (sections/cart-compare-data.liquid) and merge by line key. Any failure
     degrades to no strikethrough — it never blocks the cart from rendering. */
  function parseCompareMap(html) {
    if (!html) return {};
    try {
      var doc = new DOMParser().parseFromString(html, 'text/html');
      var node = doc.getElementById('cart-compare-data');
      return node ? (JSON.parse(node.textContent || '{}') || {}) : {};
    } catch (e) { return {}; }
  }

  function fetchCompareMap() {
    return fetch('/cart?sections=cart-compare-data', { credentials: 'same-origin' })
      .then(function(r) { return r.json(); })
      .then(function(data) { return parseCompareMap(data && data['cart-compare-data']); })
      .catch(function() { return {}; });
  }

  /* --- Cart fetch + render --- */
  function fetchCartAndRender() {
    return Promise.all([
      fetch('/cart.js', { credentials: 'same-origin' }).then(function(r) { return r.json(); }),
      fetchCompareMap()
    ])
      .then(function(res) { render(res[0], res[1]); })
      .catch(function(err) {
        if (window.console && console.warn) console.warn('[cart-drawer] fetch failed:', err);
        clear(body);
        body.appendChild(el('p', {
          style: 'padding:24px;text-align:center;color:#999;',
          text: "Couldn't load your cart."
        }));
      });
  }

  function render(cart, compareMap) {
    compareMap = compareMap || {};
    var itemCount = cart.item_count || 0;

    /* Keep the header cart count badge in sync. */
    var badge = document.querySelector('.cart-count-badge');
    if (badge) {
      badge.textContent = itemCount;
      if (itemCount > 0) badge.removeAttribute('hidden');
      else badge.setAttribute('hidden', '');
    }

    if (itemCount === 0) {
      body.hidden = true;
      footer.hidden = true;
      emptyState.hidden = false;
      return;
    }

    body.hidden = false;
    footer.hidden = false;
    emptyState.hidden = true;

    clear(body);
    (cart.items || []).forEach(function(item) {
      body.appendChild(buildLine(item, compareMap));
    });

    subtotalEl.textContent = formatMoney(cart.total_price);

    /* Retail savings badge — Σ(regular − payable) where regular = the higher of
       compare-at and the line's pre-discount price. Captures sale markdowns +
       package/code discounts in one number; mirrors the /cart footer badge. */
    var savingsEl = drawer.querySelector('[data-cart-drawer-savings]');
    if (savingsEl) {
      var regularTotal = 0;
      (cart.items || []).forEach(function(item) {
        var ca = compareMap[item.key] || 0;
        regularTotal += Math.max(ca, item.original_price) * item.quantity;
      });
      var saved = regularTotal - cart.total_price;
      if (saved > 0) {
        savingsEl.textContent = '✓ You saved ' + formatMoney(saved);
        savingsEl.style.color = '#0a8a0a';
        savingsEl.style.fontWeight = '700';
        savingsEl.style.fontSize = '13px';
        savingsEl.style.margin = '2px 0 0';
        savingsEl.hidden = false;
      } else {
        savingsEl.hidden = true;
      }
    }

    appendDeliveryEstimates(cart);
  }

  /* --- Per-item delivery-by estimates ---------------------------------------
     /cart.js has no product tags/metafields, so delivery-messaging flags come
     from the fitment API (fitment_product_index). Date math is shared via
     window.TFSDelivery (assets/delivery-estimate.js). Both dependencies are
     optional — no estimates render if either is missing. Same rules as the
     PDP/cards: no estimate for made-to-order or special-notes products;
     custom drill = +3 business days. */
  function appendDeliveryEstimates(cart) {
    var F = window.TFSFitment;
    var D = window.TFSDelivery;
    if (!F || !F.api || !D || !(cart.items || []).length) return;

    var ids = [];
    cart.items.forEach(function(item) {
      if (item.product_id && ids.indexOf(item.product_id) === -1) ids.push(item.product_id);
    });
    if (!ids.length) return;

    F.api.get('/products/delivery-flags', { ids: ids.join(',') }).then(function(data) {
      var flags = (data && data.flags) || {};
      cart.items.forEach(function(item) {
        var f = flags[String(item.product_id)];
        if (!f || f.is_made_to_order || f.has_special_notes) return;
        var line = drawer.querySelector('[data-cart-line="' + CSS.escape(item.key) + '"]');
        if (!line || line.querySelector('.cart-drawer__item-delivery')) return;
        var info = line.querySelector('.cart-drawer__item-info');
        if (!info) return;
        var datesEl = el('strong', { text: D.windowText(!!f.needs_custom_drill) });
        var delivery = el('div', { class: 'cart-drawer__item-delivery' }, [
          el('span', { text: 'Free delivery as soon as ' }), datesEl
        ]);
        var removeBtn = info.querySelector('.cart-drawer__item-remove');
        info.insertBefore(delivery, removeBtn);
      });
    }).catch(function() { /* estimates are progressive enhancement — stay quiet */ });
  }

  function buildLine(item, compareMap) {
    /* All values go through el()'s text/setAttribute paths — no innerHTML.
       Variant title and vendor are user-modifiable in Shopify admin, treat
       as untrusted. */
    var imgSrc = item.image ? (item.image.replace(/(\.\w+)(\?|$)/, '_120x$1$2')) : '';

    var img = el('div', { class: 'cart-drawer__item-img' },
      imgSrc ? [el('img', { src: imgSrc, alt: item.product_title || '', loading: 'lazy' })] : []
    );

    var titleA = el('a', { href: item.url || '#', text: item.product_title || '' });
    var titleH = el('div', { class: 'cart-drawer__item-title' }, [titleA]);

    var infoTop = el('div', null, []);
    if (item.vendor) {
      infoTop.appendChild(el('div', { class: 'cart-drawer__item-vendor', text: item.vendor }));
    }
    infoTop.appendChild(titleH);
    if (item.variant_title && item.variant_title !== 'Default Title') {
      infoTop.appendChild(el('div', { class: 'cart-drawer__item-variant', text: item.variant_title }));
    }

    /* Line-level discount titles (automatic discounts, e.g. wheel+tire
       package deals). final_line_price below is already discounted — this
       names the saving so the markdown reads as intentional. */
    if (item.line_level_discount_allocations && item.line_level_discount_allocations.length) {
      item.line_level_discount_allocations.forEach(function(alloc) {
        var title = alloc.discount_application && alloc.discount_application.title;
        if (!title) return;
        var amountTxt = alloc.amount ? '  −' + formatMoney(alloc.amount) : '';
        var label = el('div', { class: 'cart-drawer__item-discount', text: '✓ ' + title + amountTxt });
        label.style.color = '#0a8a0a';
        label.style.fontSize = '12px';
        label.style.fontWeight = '600';
        label.style.marginTop = '2px';
        infoTop.appendChild(label);
      });
    }

    /* Package member note — minimal drawer treatment (Stage-3 Task 4). The
       drawer doesn't do full re-grouping/re-sorting like the cart page; this
       is just a one-line hint so shoppers recognize a builder-added line as
       part of a package. Internal props (_pkg_id/_axle) are never rendered
       here — /cart.js's item.properties is only read for this boolean check. */
    if (item.properties && item.properties._pkg_id) {
      var pkgNote = el('div', { class: 'cart-drawer__item-package-note', text: 'Part of a Wheel & Tire Package' });
      infoTop.appendChild(pkgNote);
    }

    var minusBtn = el('button', {
      class: 'cart-drawer__qty-btn',
      'data-qty-minus': '',
      'aria-label': 'Decrease quantity',
      type: 'button',
      text: '−'
    });
    var plusBtn = el('button', {
      class: 'cart-drawer__qty-btn',
      'data-qty-plus': '',
      'aria-label': 'Increase quantity',
      type: 'button',
      text: '+'
    });
    var qtyVal = el('span', { class: 'cart-drawer__qty-value', text: String(item.quantity) });
    var qtyWrap = el('div', { class: 'cart-drawer__item-qty' }, [minusBtn, qtyVal, plusBtn]);

    /* Strike the retail line price: the higher of compare-at (× qty) and the
       pre-discount line price, so a plain sale shows its markdown even with no
       discount allocation, and a sale + package collapse into one struck price.
       The package/code name is still listed above via its allocation chip. */
    var compareAt = (compareMap && compareMap[item.key]) || 0;
    var regularLine = Math.max(compareAt * item.quantity, item.original_line_price || 0);
    var priceEl;
    if (regularLine > item.final_line_price) {
      var wasEl = el('s', { text: formatMoney(regularLine) });
      wasEl.style.opacity = '0.55';
      wasEl.style.fontWeight = '400';
      wasEl.style.marginRight = '6px';
      priceEl = el('div', { class: 'cart-drawer__item-price' }, [wasEl, document.createTextNode(formatMoney(item.final_line_price))]);
    } else {
      priceEl = el('div', { class: 'cart-drawer__item-price', text: formatMoney(item.final_line_price) });
    }
    var rowEl = el('div', { class: 'cart-drawer__item-row' }, [qtyWrap, priceEl]);

    var removeBtn = el('button', {
      class: 'cart-drawer__item-remove',
      'data-qty-remove': '',
      type: 'button',
      text: 'Remove'
    });

    var info = el('div', { class: 'cart-drawer__item-info' }, [infoTop, rowEl, removeBtn]);
    var line = el('div', { class: 'cart-drawer__item', 'data-cart-line': item.key }, [img, info]);

    minusBtn.addEventListener('click', function() { changeQty(item.key, -1); });
    plusBtn.addEventListener('click', function() { changeQty(item.key, +1); });
    removeBtn.addEventListener('click', function() { changeQty(item.key, 0, true); });

    return line;
  }

  function changeQty(lineKey, delta, removeAll) {
    var line = drawer.querySelector('[data-cart-line="' + CSS.escape(lineKey) + '"]');
    if (!line) return;
    var currentQty = parseInt(line.querySelector('.cart-drawer__qty-value').textContent, 10) || 1;
    var newQty = removeAll ? 0 : Math.max(0, currentQty + delta);

    line.querySelectorAll('button').forEach(function(b) { b.disabled = true; });

    fetch('/cart/change.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      credentials: 'same-origin',
      body: JSON.stringify({ id: lineKey, quantity: newQty })
    })
      .then(function(r) { return r.json(); })
      .then(function(cart) {
        /* Single render path: publish cart:updated and let the subscriber below
           re-render with a fresh compare-at map. Other listeners (header count)
           stay in sync too. Fallback to a direct fetch if PubSub is absent. */
        if (typeof PubSub !== 'undefined' && typeof PubSub.publish === 'function') {
          PubSub.publish('cart:updated', cart);
        } else {
          fetchCartAndRender();
        }
      })
      .catch(function(err) {
        if (window.console && console.warn) console.warn('[cart-drawer] qty change failed:', err);
        line.querySelectorAll('button').forEach(function(b) { b.disabled = false; });
      });
  }

  /* --- Event wiring --- */

  /* Open via cart icon click.
     IMPORTANT: ignore clicks originating inside the drawer itself, otherwise
     the drawer's own "View Cart & Checkout" anchor (href="/cart") gets caught
     by the `a[href="/cart"]` matcher, preventDefault'd, and never navigates. */
  document.addEventListener('click', function(e) {
    if (e.target.closest('.cart-drawer')) return;
    var trigger = e.target.closest('[data-cart-drawer-open], .header__cart, .cart-icon, a[href="/cart"]');
    if (trigger) {
      if (onCartPage()) return;
      e.preventDefault();
      open();
    }
  });

  /* Close via overlay, X button, Escape */
  drawer.addEventListener('click', function(e) {
    if (e.target.closest('[data-cart-drawer-close]')) close();
  });

  document.addEventListener('keydown', function(e) {
    if (e.key === 'Escape' && isOpen) close();
  });

  /* Subscribe to cart:updated PubSub for auto-open after add-to-cart.
     global.js publishes via `PubSub.publish('cart:updated')` with no data,
     so we always fetch fresh on the open.
     Guard: never auto-open while already on the /cart page (the cart is on
     screen already) — mirrors the cart-icon click handler above. We still
     render() so the drawer contents + header badge stay in sync. */
  function subscribeWhenReady() {
    if (typeof PubSub !== 'undefined' && typeof PubSub.subscribe === 'function') {
      PubSub.subscribe('cart:updated', function(data) {
        if (data && typeof data === 'object' && 'items' in data) {
          /* Full cart object passed (this drawer's qty change, /cart page,
             upsell, package flow). The /cart page owns its own render, so skip
             when the drawer is closed there (header count handled in global.js).
             Otherwise refresh the compare-at map, then render with strikethroughs. */
          if (!isOpen && onCartPage()) return;
          fetchCompareMap().then(function(map) {
            render(data, map);
            if (!isOpen && !onCartPage()) open();
          });
        } else {
          /* Most callers pass nothing — open + fetch fresh state (incl. map). */
          if (isOpen) fetchCartAndRender();
          else if (!onCartPage()) open();
        }
      });
    } else {
      setTimeout(subscribeWhenReady, 50);
    }
  }
  subscribeWhenReady();

  window.TFSCartDrawer = { open: open, close: close, render: render };
})();
